import { useEffect, useRef } from 'react';
import {
  Box,
  Button,
  Checkbox,
  Flex,
  Form,
  PanelHeader,
  PanelSection,
  PanelTitle,
  Select,
  TextField,
} from '@customerio/pluma-components/react';
import { z } from '@customerio/hydra-validation';
import { Controller, useWatch, useZodForm } from '@/components/form';
import { useSignupFlow } from '../signup-flow';
import { companyInfoSubmittedProps, SignupAnalyticsEvent, signupTypeProps } from '../utils/analytics';
import { ROLE_OPTIONS } from './company-role-options';
import { withFlowWriteThrough } from './flow-write-through';

/** Rejects names that smuggle a URL (e.g. "win.customer.io"). */
const URL_IN_NAME_REGEX = /\S+\.\w{2,}/;

// Validation stays refine-only: values are stored raw (no trim/transform), and
// the website rule is conditional on the no-website escape hatch.
const companyStepSchema = z
  .object({
    firstName: z
      .string()
      .refine((value) => value.trim() !== '', "First name can't be blank")
      .refine((value) => !URL_IN_NAME_REGEX.test(value), 'First name cannot contain a URL'),
    lastName: z.string().refine((value) => !URL_IN_NAME_REGEX.test(value), 'Last name cannot contain a URL'),
    companyName: z
      .string()
      .refine((value) => value.trim() !== '', "Company name can't be blank")
      .refine((value) => value.length <= 191, "Company name can't be longer than 191 characters"),
    role: z.string().min(1, "Role can't be blank"),
    companyUrl: z.string(),
    noWebsite: z.boolean(),
  })
  .superRefine((data, ctx) => {
    if (!data.noWebsite && data.companyUrl.trim() === '') {
      ctx.addIssue({ code: 'custom', path: ['companyUrl'], message: "Website can't be blank" });
    }
  });

type CompanyStepValues = z.infer<typeof companyStepSchema>;

/** The text fields whose form key doubles as their flow-state key. */
type CompanyTextField = 'firstName' | 'lastName' | 'companyName' | 'companyUrl';

export function CompanyStep() {
  const {
    formValues,
    patchFormValues,
    onboardingRole,
    setOnboardingRole,
    effectiveSignupType,
    goToNextStep,
    trackSignupEvent,
  } = useSignupFlow();

  const form = useZodForm({
    schema: companyStepSchema,
    // Seeds the Clearbit prepopulation (companyName) and restores any
    // previously-typed values when navigation remounts the step.
    defaultValues: {
      firstName: formValues.firstName,
      lastName: formValues.lastName,
      companyName: formValues.companyName,
      role: onboardingRole ?? '',
      companyUrl: formValues.companyUrl,
      noWebsite: formValues.noWebsite,
    },
  });

  // Fire once per step entry — the analog of Ember's fire-on-route-transition
  // analytics; the ref keeps StrictMode's double-invoked effects from
  // double-counting the funnel.
  const hasTrackedView = useRef(false);
  useEffect(() => {
    if (hasTrackedView.current) {
      return;
    }
    hasTrackedView.current = true;
    trackSignupEvent(SignupAnalyticsEvent.CompanyInfoViewed, signupTypeProps({ signupType: effectiveSignupType }));
  }, [trackSignupEvent, effectiveSignupType]);

  // Every change writes through raw — the register call owns the no-website
  // blanking.
  const registerCompanyField = (name: CompanyTextField) =>
    withFlowWriteThrough(form.registerPluma(name), name, patchFormValues);

  const [role, companyUrl, noWebsite] = useWatch({
    control: form.control,
    name: ['role', 'companyUrl', 'noWebsite'],
  });
  const isWebsiteSatisfied = noWebsite || companyUrl.trim() !== '';
  // Role and website gate the button outright; field errors only gate it after
  // a submit attempt — a first click on blank names surfaces the errors.
  const isSubmitDisabled =
    role === '' || !isWebsiteSatisfied || (form.formState.submitCount > 0 && !form.formState.isValid);

  const onSubmit = form.handleSubmit(async (values: CompanyStepValues) => {
    trackSignupEvent(
      SignupAnalyticsEvent.CompanyInfoSubmitted,
      companyInfoSubmittedProps({
        signupType: effectiveSignupType,
        companyName: values.companyName,
        website: values.companyUrl,
        haveWebsite: !values.noWebsite,
        role: values.role,
      }),
    );
    await goToNextStep();
  });

  return (
    <Box data-testid="signup-step-company">
      <PanelHeader>
        <PanelTitle level="1">Set up Customer.io</PanelTitle>
      </PanelHeader>
      <PanelSection>
        <Form onSubmit={onSubmit}>
          <Flex mb="300" gap="100">
            <TextField {...registerCompanyField('firstName')} flex={1} label="First name" autofocus={true} />
            <TextField {...registerCompanyField('lastName')} flex={1} label="Last name" />
          </Flex>

          <Box mb="300">
            <Controller
              name="role"
              control={form.control}
              render={({ field, fieldState }) => (
                <Select
                  label="Role"
                  name={field.name}
                  value={field.value}
                  onChange={(value) => {
                    field.onChange(value ?? '');
                    setOnboardingRole(value);
                  }}
                  onBlur={field.onBlur}
                  error={fieldState.error?.message}
                  options={ROLE_OPTIONS}
                  placeholder="Select a role"
                />
              )}
            />
          </Box>

          <Box mb="300">
            <TextField {...registerCompanyField('companyName')} label="Company name" placeholder="Acme, Inc." />
          </Box>

          <Box mb="300">
            <TextField {...registerCompanyField('companyUrl')} label="Website" placeholder="https://example.com" />
            <Controller
              name="noWebsite"
              control={form.control}
              render={({ field }) => (
                <Checkbox
                  mt="100"
                  label="I do not have a website"
                  isChecked={field.value}
                  onCheckedChange={(checked) => {
                    field.onChange(checked);
                    patchFormValues({ noWebsite: checked });
                    void form.trigger('companyUrl');
                  }}
                />
              )}
            />
          </Box>

          <Flex justifyContent="flex-end" mt="200">
            <Button
              type="submit"
              variant="primary"
              icon="chevron-right"
              iconPosition="trailing"
              isDisabled={isSubmitDisabled}
              isLoading={form.formState.isSubmitting}
            >
              Continue
            </Button>
          </Flex>
        </Form>
      </PanelSection>
    </Box>
  );
}
